import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScopeEditor } from "@/components/ScopeEditor";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";

// Bulk set scope + standardize names + assign missing PR numbers across pipeline items,
// with a dry-run preview (POST /pipeline/bulk-scope-preview) before committing.
export function BulkScopeDialog({ open, onOpenChange, items = [], onApply }) {
  const [scopes, setScopes] = useState([]);
  const [rename, setRename] = useState(true);
  const [assignNumbers, setAssignNumbers] = useState(true);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => { setScopes([]); setRename(true); setAssignNumbers(true); setPreview(null); };
  const partialNeedsNote = scopes.some((s) => s.completeness === "partial" && !(s.completeness_note || "").trim());

  const payload = () => {
    const p = { items, rename, assign_numbers: assignNumbers };
    if (scopes.length) p.scopes = scopes;
    return p;
  };

  const runPreview = async () => {
    if (partialNeedsNote) { toast.error("Describe each partial scope first."); return; }
    setBusy(true);
    try { const r = await api.post("/pipeline/bulk-scope-preview", payload()); setPreview(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not preview"); }
    finally { setBusy(false); }
  };

  const apply = async () => {
    if (partialNeedsNote) { toast.error("Describe each partial scope first."); return; }
    setBusy(true);
    try { await onApply(payload()); reset(); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent data-testid="bulk-scope-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Set scope &amp; standardize · {items.length} project{items.length === 1 ? "" : "s"}</DialogTitle>
          <DialogDescription>Set the scopes to apply to every selected project (leave empty to keep each project’s own scopes and just rename/number). Preview before committing.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 max-h-[55vh] overflow-y-auto pr-1">
          <div>
            <div className="text-xs font-medium text-slate-500 mb-1.5">Scopes to apply <span className="text-slate-400 font-normal">(optional)</span></div>
            <ScopeEditor value={scopes} onChange={setScopes} />
          </div>
          <div className="space-y-2.5 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <Checkbox checked={rename} onCheckedChange={(v) => setRename(!!v)} data-testid="bulk-scope-rename" /> Rename to the standard (name + lead scope + completeness)
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <Checkbox checked={assignNumbers} onCheckedChange={(v) => setAssignNumbers(!!v)} data-testid="bulk-scope-number" /> Assign PR-#### numbers to any without one
            </label>
          </div>
          {preview && (
            <div className="rounded-lg border border-[#003E87]/20 bg-[#003E87]/5 p-3 text-sm" data-testid="bulk-scope-preview">
              <div className="font-medium text-[#003E87] mb-1.5">{preview.total} project{preview.total === 1 ? "" : "s"} · {preview.renamed} will be renamed · {preview.numbered} will get a number</div>
              {preview.samples?.length > 0 ? (
                <div className="space-y-1">
                  {preview.samples.map((s, i) => (
                    <div key={i} className="flex items-center gap-1.5 text-xs text-slate-600" data-testid={`bulk-scope-sample-${i}`}>
                      <span className="truncate max-w-[40%] text-slate-400">{s.from}</span>
                      <ArrowRight className="w-3 h-3 shrink-0 text-slate-400" />
                      <span className="truncate font-medium text-slate-800">{s.to}</span>
                    </div>
                  ))}
                </div>
              ) : <div className="text-xs text-slate-500">No name changes — names are already standard.</div>}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="bulk-scope-cancel">Cancel</Button>
          <Button variant="outline" onClick={runPreview} disabled={busy} data-testid="bulk-scope-preview-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Preview"}
          </Button>
          <Button onClick={apply} disabled={busy} data-testid="bulk-scope-apply" className="bg-[#003E87] hover:bg-[#00305F] text-white">Apply to {items.length}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
